import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExternalLink, ShieldCheck, Globe, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
interface SetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const WORKER_CODE = `export default {
  async email(message, env, ctx) {
    const { from, to } = message;
    // Config: Thay đổi các giá trị này để khớp với ứng dụng của bạn
    const API_URL = "https://YOUR_APP_URL.workers.dev/api/internal/ingest-email";
    const SECRET_TOKEN = "internal-secret-token";
    const payload = {
      inboxId: to, // API sẽ tự động tìm UUID dựa trên address
      fromName: from.split('<')[0]?.trim() || from,
      fromAddress: from,
      subject: message.headers.get("subject") || "(Không có chủ đề)",
      bodyText: "Email được nhận qua Cloudflare Routing. Nội dung đầy đủ được gửi trong raw data.",
      preview: "Email mới từ " + from,
      receivedAt: Date.now()
    };
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": \`Bearer \${SECRET_TOKEN}\`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) console.error("Ingest failed:", await res.text());
    } catch (e) {
      console.error("Worker error:", e);
    }
  }
}
`;
export function SetupModal({ isOpen, onClose }: SetupModalProps) {
  const [copied, setCopied] = React.useState(false);
  const copyCode = () => {
    navigator.clipboard.writeText(WORKER_CODE);
    setCopied(true);
    toast.success('Đã sao chép mã nguồn!');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[650px] glass-card border-white/10 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            Kết nối Tên miền riêng
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Sử dụng Cloudflare Email Routing để nhận email trên tên miền của chính bạn.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2 text-foreground">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">1</div>
              Bật Email Routing
            </h4>
            <p className="text-sm text-muted-foreground pl-8">
              Truy cập Cloudflare Dashboard, chọn tên miền và đi tới <strong>Email {'->'} Email Routing</strong>. Nhấn "Enable Email Routing".
            </p>
          </div>
          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2 text-foreground">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">2</div>
              Triển khai Worker Ingestion
            </h4>
            <div className="pl-8 space-y-3">
              <p className="text-sm text-muted-foreground">
                Tạo một Worker mới và dán mã bên dưới. Thay đổi <code>YOUR_APP_URL</code> thành địa chỉ web của bạn.
              </p>
              <div className="relative group">
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute right-2 top-2 z-10 hover:bg-white/10"
                  onClick={copyCode}
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
                <ScrollArea className="h-48 w-full rounded-md border border-white/10 bg-black/40 p-4">
                  <pre className="text-[11px] font-mono text-violet-300">
                    {WORKER_CODE}
                  </pre>
                </ScrollArea>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2 text-foreground">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">3</div>
              Cấu hình Routing Rules
            </h4>
            <p className="text-sm text-muted-foreground pl-8">
              Trong tab "Routing Rules", thêm một Catch-all address và chọn Action là <strong>Send to Worker</strong>, sau đó chọn Worker vừa tạo.
            </p>
          </div>
          <div className="bg-primary/10 p-4 rounded-xl border border-primary/20 flex gap-4">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-primary">Tự động hóa & Bảo mật</p>
              <p className="text-muted-foreground">Mọi email gửi đến bất kỳ tiền tố nào của domain sẽ tự động tạo một inbox ảo tại đây.</p>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Đóng</Button>
          <Button className="btn-purple w-full sm:w-auto gap-2" asChild>
            <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer">
              Đi tới Cloudflare <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}